// src/components/VoiceRecordButton.js
import React, { useEffect, useRef } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { 
  FaMicrophone, 
  FaStop, 
  FaPause, 
  FaPlay,
  FaTimes,
  FaSpinner 
} from 'react-icons/fa';

const VoiceRecordButton = ({
  isRecording,
  isPaused,
  isTranscribing,
  transcribeProgress = 0,
  isSupported,
  recordingDuration,
  formattedDuration,
  audioLevel,
  error,
  onStart,
  onStop,
  onPause,
  onResume,
  onCancel,
  disabled = false
}) => {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const levelRef = useRef(audioLevel);

  // Keep level ref in sync for canvas animation
  useEffect(() => {
    levelRef.current = audioLevel;
  }, [audioLevel]);

  // Animated waveform on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isRecording || isPaused) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      return;
    }

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    let phase = 0;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const level = levelRef.current / 100;
      const amplitude = 4 + level * (H / 2 - 4);

      ctx.beginPath();
      ctx.strokeStyle = level > 0.6 ? '#ff4757' : '#00E5FF';
      ctx.lineWidth = 2;

      for (let x = 0; x < W; x++) {
        const y = H / 2 + 
          Math.sin((x / W) * Math.PI * 4 + phase) * amplitude * 0.6 +
          Math.sin((x / W) * Math.PI * 8 + phase * 1.5) * amplitude * 0.3 +
          Math.sin((x / W) * Math.PI * 2 + phase * 0.5) * amplitude * 0.1;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }

      ctx.stroke();
      phase += 0.15;
      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isRecording, isPaused]);

  if (!isSupported) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Voice recording not supported in this browser</Tooltip>}
      >
        <span>
          <Button variant="outline-secondary" disabled className="voice-btn">
            <FaMicrophone size={16} />
          </Button>
        </span>
      </OverlayTrigger>
    );
  }

  if (isTranscribing) {
    return (
      <div className="voice-transcribing w-100 flex-grow-1">
        <Button variant="outline-info" disabled className="voice-btn-stop" style={{border: 'none'}}>
          <FaSpinner className="voice-spinner" />
        </Button>
        <div className="voice-transcribing-info flex-grow-1 ms-2">
          <span className="voice-transcribing-label text-info small">
            {transcribeProgress > 0
              ? `Transcribing… ${transcribeProgress}%`
              : 'Transcribing…'}
          </span>
          {transcribeProgress > 0 && (
            <div className="voice-progress-bar mt-1" style={{height: '4px', background: 'rgba(0, 229, 255, 0.2)', borderRadius: '2px'}}>
              <div
                className="voice-progress-fill h-100"
                style={{ width: `${transcribeProgress}%`, background: 'var(--electric-cyan)', borderRadius: '2px', transition: 'width 0.3s' }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="voice-recording-controls w-100 flex-grow-1">
        {/* Waveform */}
        <div className="voice-waveform-wrapper">
          <canvas 
            ref={canvasRef} 
            width={100} 
            height={28}
            className="voice-waveform-canvas"
          />
          <span className={`voice-duration ${recordingDuration > 90 ? 'warning' : ''}`}>
            {formattedDuration}
          </span>
        </div>

        <div className="d-flex gap-2">
            {/* Pause/Resume */}
            <OverlayTrigger placement="top" overlay={
              <Tooltip>{isPaused ? 'Resume' : 'Pause'}</Tooltip>
            }>
              <Button 
                  variant="outline-secondary" 
                  onClick={isPaused ? onResume : onPause}
                  className="voice-btn-stop rounded-circle d-flex align-items-center justify-content-center"
              >
                  {isPaused ? <FaPlay size={10} /> : <FaPause size={10} />}
              </Button>
            </OverlayTrigger>

            {/* Stop & transcribe */}
            <OverlayTrigger placement="top" overlay={<Tooltip>Stop & transcribe</Tooltip>}>
              <Button 
                  variant="danger" 
                  onClick={onStop}
                  className="voice-btn-stop rounded-circle d-flex align-items-center justify-content-center"
              >
                  <FaStop size={10} />
              </Button>
            </OverlayTrigger>

            {/* Cancel */}
            <OverlayTrigger placement="top" overlay={<Tooltip>Cancel</Tooltip>}>
              <Button 
                  variant="outline-danger" 
                  onClick={onCancel}
                  className="voice-btn-stop rounded-circle d-flex align-items-center justify-content-center"
              >
                  <FaTimes size={10} />
              </Button>
            </OverlayTrigger>
        </div>
      </div>
    );
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{error ? 'Error connecting microphone' : 'Record voice message'}</Tooltip>}
    >
      <Button
        variant={error ? 'danger' : 'secondary'}
        onClick={onStart}
        disabled={disabled}
        className="voice-btn"
      >
        <FaMicrophone size={16} />
      </Button>
    </OverlayTrigger>
  );
};

export default VoiceRecordButton;